/**
 * The men the cursor can land on (ROUND7 §2), for `aimPick`: every soldier on his feet, with the height of the
 * ground he stands on (a man in a trench is drawn lower, one on a rise higher). ROUND8: and every dog, at a dog's
 * drawn size: with a man's 2.3 m silhouette, a cursor 1.4 m clear above a dog still picked it.
 *
 * Left out: the dead, and the knocked-down and wounded, who are drawn lying flat - the cursor on one of them is
 * already the ground by him; and machines, which are drawn nothing like a 0.9 x 1.8 m man and are big enough to
 * hit where they stand.
 */

import { isAutomated, isDog } from '../sim/enemies/specials/kinds';
import type { Enemy, MissionDef, PropState } from '../sim/types';
import { type AimBody, BODY_SLACK } from './aimPick';

/**
 * How a dog is drawn, metres (render/characters/dogModel.ts, built at its drawn size): 0.92 m to its ear tips
 * standing, 1.37 m from nose to tail and 0.3 m across. `tests/unit/dogHudAim.test.ts` holds these to the model.
 */
export const DOG_DRAWN = { height: 0.95, halfLength: 0.7, halfBreadth: 0.15 } as const;
/** However it faces, the cursor gets at least this much either side of a dog, metres: end-on it is a thin mark. */
export const DOG_MIN_HALF_WIDTH = 0.3;

/**
 * A dog's body for `aimPick`, standing on `y`, as long as it is drawn the way it faces: side-on its length runs
 * across the screen, end-on (nose to the camera or away) its nose or tail reaches half its length up or down it
 * past where it stands - so that is the slack at the ends of its height. The sim strikes it as a disc at its
 * middle, which is where `aimPick` hands the aim, so a cursor on its nose sends the round into its chest.
 */
export function dogBody(e: Pick<Enemy, 'x' | 'z' | 'facing'>, y?: number): AimBody {
  const across = Math.abs(Math.sin(e.facing));
  const along = Math.abs(Math.cos(e.facing));
  const { height, halfLength, halfBreadth } = DOG_DRAWN;
  const halfWidth = Math.max(DOG_MIN_HALF_WIDTH, halfLength * across + halfBreadth * along);
  const slack = Math.max(BODY_SLACK, halfLength * along + halfBreadth * across);
  const body: AimBody = { x: e.x, z: e.z, height, halfWidth, slack };
  if (y !== undefined) body.y = y;
  return body;
}

/** `groundY` is the height a person stands at (`StaticWorld.groundY`); without it every man stands at y = 0. */
export function aimTargets(enemies: readonly Enemy[], groundY?: (x: number, z: number) => number): AimBody[] {
  const out: AimBody[] = [];
  for (const e of enemies) {
    if (e.state === 'dead' || e.state === 'wounded' || isAutomated(e.kind)) continue;
    const y = groundY?.(e.x, e.z);
    // A living dog is always 'down' - the state the infantry code leaves alone (enemies/create.ts) - and on its feet.
    if (isDog(e.kind)) out.push(dogBody(e, y));
    else if (e.state !== 'down') out.push(y === undefined ? { x: e.x, z: e.z } : { x: e.x, z: e.z, y });
  }
  return out;
}

/**
 * The searchlight poles still standing, as things the cursor can land on. A round only puts a lamp out if it
 * strikes the pole where it stands, but the lamp is drawn 3 m up it: at the game's 45 deg a cursor on the lamp
 * aimed 3 m past the pole, and every round went wide (reported in play on the training range, 2026-09-23).
 * Drawn size from render/kit/fieldworks.ts: a pole with its drum about 3.3 m up and 0.76 m across.
 */
export function lampTargets(
  def: Pick<MissionDef, 'props'>, props: readonly PropState[], groundY?: (x: number, z: number) => number,
): AimBody[] {
  const down = new Set(props.filter((p) => p.destroyed).map((p) => p.id));
  const out: AimBody[] = [];
  for (const p of def.props) {
    if (p.kind !== 'searchlight' || down.has(p.id)) continue;
    out.push({ x: p.x, z: p.z, y: groundY?.(p.x, p.z) ?? 0, height: LAMP_HEIGHT, halfWidth: LAMP_HALF_WIDTH });
  }
  return out;
}

/** How a searchlight is drawn: its drum tops out about 3.3 m up, 0.76 m across. */
const LAMP_HEIGHT = 3.4;
const LAMP_HALF_WIDTH = 0.45;
